import { type FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { z } from 'zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { type RoleKey, tenantUserRoleOptions } from '@/types/tenantUserRole';
import { type RowEntity } from '@/hooks/data/datatable/useTableStore';
import { type Invitation } from '@/types/invitations';
import { Input } from '@/components/ui/input';
import { useTranslations } from 'next-intl';

export const InvitationFields: FC<{ invitation: RowEntity }> = ({ invitation }) => {
  const { inviter, invitee, roles, createdAt, expiresAt } = invitation.original as Invitation;

  const role = roles[0];
  const tRoles = useTranslations('users.roles');
  const tInvitation = useTranslations('users.invitationFields');

  const formSchema = z.object({
    inviter: z.string(),
    invitee: z.string(),
    role: z.string(),
    createdAt: z.string(),
    expiresAt: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      invitee: '',
      inviter: '',
      role: '',
      createdAt: '',
      expiresAt: '',
    },
  });

  useEffect(() => {
    form.reset({
      invitee: invitee ?? '',
      inviter: inviter,
      role: role,
      createdAt: format(createdAt, 'LLL dd, y'),
      expiresAt: format(expiresAt, 'LLL dd, y'),
    });
  }, [form, invitee, inviter, role, createdAt, expiresAt]);

  const { setValue } = form;

  function onSubmit() {
    // Don't do anything
  }

  return (
    <div className="p-3">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="invitee"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tInvitation('email')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      aria-invalid={Boolean(form.formState.errors.invitee)}
                      className="bg-input-background focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="role"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tInvitation('role')}</FormLabel>
                <FormControl>
                  <Select onValueChange={(value) => setValue('role', value)} disabled={true}>
                    <SelectTrigger className="bg-input-background focus:duration-0">
                      <SelectValue placeholder={field.value ? tRoles(field.value as RoleKey) : ''} />
                    </SelectTrigger>
                    <SelectContent>
                      {tenantUserRoleOptions.map(({ value }) => (
                        <SelectItem key={value} value={value}>
                          <div>{tRoles(value as RoleKey)}</div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="inviter"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tInvitation('invitedBy')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      aria-invalid={Boolean(form.formState.errors.invitee)}
                      className="bg-input-background focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="createdAt"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tInvitation('invitedOn')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      aria-invalid={Boolean(form.formState.errors.invitee)}
                      className="bg-input-background focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expiresAt"
            disabled={true}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{tInvitation('expiresOn')}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      aria-invalid={Boolean(form.formState.errors.invitee)}
                      className="bg-input-background focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  );
};
